import React, { useState, useEffect } from 'react';
import { Download, ShoppingBag, Settings, MessageCircle, LogOut } from 'lucide-react';

interface Purchase {
  id: string;
  productName: string;
  purchaseDate: string;
  amount: number;
  downloadUrl: string;
  downloaded: boolean;
  downloadCount: number;
}

interface UserData {
  id: string;
  email: string;
  name: string;
  joinDate: string;
  purchases: Purchase[];
}

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('downloads');
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch user data from API
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          window.location.href = '/login';
          return;
        }

        const response = await fetch('/api/auth/profile', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          localStorage.removeItem('authToken');
          window.location.href = '/login';
          return;
        }

        const data = await response.json();
        setUser(data.user);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setLoading(false);
        setUser({
          id: 'demo-user',
          email: 'demo@obelizco.com',
          name: 'Usuario Demo',
          joinDate: new Date().toISOString(),
          purchases: [
            {
              id: '1',
              productName: '21 Días de Desescolarización',
              purchaseDate: new Date().toISOString(),
              amount: 15.00,
              downloadUrl: '/download/demo-token',
              downloaded: false,
              downloadCount: 0
            }
          ]
        });
      }
    };

    fetchUserData();
  }, []);

  const handleDownload = (purchase: Purchase) => {
    window.open(purchase.downloadUrl, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando tu dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Obelizco Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">Hola, {user?.name}</span>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 text-gray-500 hover:text-gray-700"
              >
                <LogOut className="h-4 w-4" />
                <span>Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64">
            <nav className="bg-white rounded-lg shadow p-4">
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setActiveTab('downloads')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left ${
                      activeTab === 'downloads'
                        ? 'bg-red-50 text-red-700 border-l-4 border-red-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Download className="h-5 w-5" />
                    <span>Mis Descargas</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setActiveTab('products')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left ${
                      activeTab === 'products'
                        ? 'bg-red-50 text-red-700 border-l-4 border-red-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <ShoppingBag className="h-5 w-5" />
                    <span>Productos</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setActiveTab('account')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left ${
                      activeTab === 'account'
                        ? 'bg-red-50 text-red-700 border-l-4 border-red-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Settings className="h-5 w-5" />
                    <span>Mi Cuenta</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setActiveTab('support')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left ${
                      activeTab === 'support'
                        ? 'bg-red-50 text-red-700 border-l-4 border-red-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <MessageCircle className="h-5 w-5" />
                    <span>Soporte</span>
                  </button>
                </li>
              </ul>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === 'downloads' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Mis Descargas</h2>
                  <p className="text-sm text-gray-600">Accede a todos tus productos digitales</p>
                </div>
                <div className="p-6">
                  {user?.purchases.length === 0 ? (
                    <div className="text-center py-8">
                      <Download className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No tienes descargas disponibles</p>
                      <button
                        onClick={() => setActiveTab('products')}
                        className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                      >
                        Explorar Productos
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {user?.purchases.map((purchase) => (
                        <div key={purchase.id} className="border rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium text-gray-900">{purchase.productName}</h3>
                              <p className="text-sm text-gray-600">
                                Comprado el {new Date(purchase.purchaseDate).toLocaleDateString('es-ES')}
                              </p>
                              <p className="text-sm text-gray-500">
                                Descargado {purchase.downloadCount} vez{purchase.downloadCount !== 1 ? 'es' : ''}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-semibold text-gray-900">${purchase.amount}</p>
                              <button
                                onClick={() => handleDownload(purchase)}
                                className="mt-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm"
                              >
                                Descargar
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Productos Disponibles</h2>
                  <p className="text-sm text-gray-600">Descubre más contenido digital</p>
                </div>
                <div className="p-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-md mb-4"></div>
                      <h3 className="font-medium text-gray-900 mb-2">21 Días de Desescolarización</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Guía completa para transformar la educación de tus hijos
                      </p>
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-gray-900">$15.00</span>
                        <button className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm">
                          Comprar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'account' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Mi Cuenta</h2>
                  <p className="text-sm text-gray-600">Gestiona tu información personal</p>
                </div>
                <div className="p-6">
                  <form className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre completo
                      </label>
                      <input
                        type="text"
                        defaultValue={user?.name}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        defaultValue={user?.email}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contraseña
                      </label>
                      <input
                        type="password"
                        placeholder="••••••••"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700"
                      >
                        Guardar Cambios
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'support' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Soporte</h2>
                  <p className="text-sm text-gray-600">¿Necesitas ayuda? Estamos aquí para ti</p>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-2">Preguntas Frecuentes</h3>
                      <div className="space-y-2">
                        <details className="cursor-pointer">
                          <summary className="text-sm text-gray-700 hover:text-gray-900">
                            ¿Cómo descargo mi producto?
                          </summary>
                          <p className="text-sm text-gray-600 mt-2 pl-4">
                            Ve a la sección "Mis Descargas" y haz clic en el botón "Descargar" del producto que compraste.
                          </p>
                        </details>
                        <details className="cursor-pointer">
                          <summary className="text-sm text-gray-700 hover:text-gray-900">
                            ¿Puedo descargar el producto varias veces?
                          </summary>
                          <p className="text-sm text-gray-600 mt-2 pl-4">
                            Sí, puedes acceder a tus descargas en cualquier momento desde tu dashboard.
                          </p>
                        </details>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-gray-900 mb-4">Contactar Soporte</h3>
                      <form className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Asunto
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder="Describe brevemente tu consulta"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mensaje
                          </label>
                          <textarea
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder="Describe tu problema o consulta en detalle"
                          ></textarea>
                        </div>
                        <button
                          type="submit"
                          className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700"
                        >
                          Enviar Mensaje
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
